import { useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical, X } from "lucide-react";

export default function CreatableSelectDnd({
  value = [],
  onChange,
  placeholder = "Type and press Enter to add...",
  theme,
}) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && inputValue.trim()) {
        e.preventDefault();
        const newItem = inputValue.trim();
        if (!value.includes(newItem)) {
          onChange([...value, newItem]);
        }
        setInputValue("");
      }
    },
    [inputValue, value, onChange],
  );

  const handleRemove = useCallback(
    (item) => {
      onChange(value.filter((v) => v !== item));
    },
    [value, onChange],
  );

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const items = Array.from(value);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      onChange(items);
    },
    [value, onChange],
  );

  return (
    <div className="rounded-md border border-gray-300 p-2 focus-within:ring-1 focus-within:ring-blue-400">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 min-h-[28px]">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="tags" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex flex-wrap gap-2 mb-2 min-h-[28px]"
                >
                  {value.map((item, index) => (
                    <Draggable key={item} draggableId={item} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`inline-flex items-center gap-1 rounded-md bg-blue-100 text-blue-700 px-2 py-1 text-sm select-none ${
                            snapshot.isDragging
                              ? "shadow-lg ring-2 ring-blue-300"
                              : ""
                          }`}
                        >
                          <span
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700"
                          >
                            <GripVertical size={14} />
                          </span>
                          <span>{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemove(item)}
                            className="text-gray-500 hover:text-red-500 ml-0.5"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : "Add more..."}
        className={`w-full min-h-[36px] px-2 py-1 border rounded-md outline-none text-sm placeholder-gray-400
    ${theme === "dark" ? "bg-gray-800 text-gray-100 border-gray-600" : "bg-white text-gray-900 border-gray-300"}
  `}
      />
    </div>
  );
}
